import { Finding, FindingStatus } from '@consigli/types';
import { useState, useEffect } from 'react';

import { EditModal, TextArea, Field, Fieldset } from '@/components';

interface EditFindingModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  finding: Finding;
  onConfirm: (updatedData: Partial<Finding>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

interface EditableFinding {
  content: string;
  refContent: string;
  messageTemplate: string;
  status: FindingStatus;
  labels: string[];
}

export const EditFindingModal = ({
  isOpen,
  onOpenChange,
  finding,
  onConfirm,
  onCancel,
  isSubmitting = false,
}: EditFindingModalProps) => {
  const [editableData, setEditableData] = useState<EditableFinding>({
    content: '',
    refContent: '',
    messageTemplate: '',
    status: FindingStatus.ACTIVE,
    labels: [],
  });

  useEffect(() => {
    if (finding) {
      setEditableData({
        content: finding.content || '',
        refContent: finding.refContent || '',
        messageTemplate: finding.messageTemplate || '',
        status: finding.status,
        labels: finding.labels || [],
      });
    }
  }, [finding]);

  const handleContentChange = (content: string) => {
    setEditableData((prev) => ({ ...prev, content }));
  };

  const handleRefContentChange = (refContent: string) => {
    setEditableData((prev) => ({ ...prev, refContent }));
  };

  const handleMessageTemplateChange = (messageTemplate: string) => {
    setEditableData((prev) => ({ ...prev, messageTemplate }));
  };

  const isValid = editableData.content.trim().length > 0;

  const handleConfirm = () => {
    onConfirm(editableData);
  };

  const handleCancel = () => {
    onCancel();
    if (finding) {
      setEditableData({
        content: finding.content || '',
        refContent: finding.refContent || '',
        messageTemplate: finding.messageTemplate || '',
        status: finding.status,
        labels: finding.labels || [],
      });
    }
  };

  return (
    <EditModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Edit Finding"
      description="Update the finding details below."
      data={editableData}
      onDataChange={setEditableData}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
      submitLabel="Save Changes"
      cancelLabel="Cancel"
      isValid={isValid}
    >
      <div className="flex flex-col gap-4">
        <Fieldset legend="Finding content">
          <Field label="Content" htmlFor="content">
            <TextArea
              id="content"
              value={editableData.content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Enter finding content..."
              rows={4}
              fullWidth
            />
          </Field>

          {finding?.refContent && (
            <Field label="Reference Content" htmlFor="refContent">
              <TextArea
                id="refContent"
                value={editableData.refContent}
                onChange={(e) => handleRefContentChange(e.target.value)}
                placeholder="Edit reference content..."
                rows={4}
                fullWidth
              />
            </Field>
          )}
        </Fieldset>
        <Fieldset legend="Conflict Message">
          <Field label="Conflict Message" htmlFor="messageTemplate">
            <TextArea
              id="messageTemplate"
              value={editableData.messageTemplate}
              onChange={(e) => handleMessageTemplateChange(e.target.value)}
              placeholder="Edit conflict message..."
              fullWidth
              rows={3}
            />
          </Field>
        </Fieldset>
      </div>
    </EditModal>
  );
};
